<template><div><h1 id="aggregierte-buchungen-generieren" tabindex="-1"><a class="header-anchor" href="#aggregierte-buchungen-generieren"><span>Aggregierte Buchungen generieren</span></a></h1>
<p>Aggregierte Buchungen generieren — Aggregieren von Einzelbuchungen für Fibu-Export</p>
<h2 id="beschreibung" tabindex="-1"><a class="header-anchor" href="#beschreibung"><span>Beschreibung</span></a></h2>
<p>Dieses Programm führt die im System vorhandenen Einzelbuchungen zusammen, so dass sie in kumulierter Form (in einer Datei) an die Finanzbuchhaltung übergeben werden können. Das Programm stellt sicher, dass jede Buchung nur 1-mal an die Finanzbuchhaltung übergeben wird. Zu diesem Zweck werden aggregierte Buchungen mit einer Journalnummer versehen. Als Datengrundlage für die Aggregierung dienen nicht die Daten in den <RouteLink to="/referenz/refs/ft.refdata.manbuch.html" title="Manuelle Buchung">Manuellen Buchungen</RouteLink> (Struktur Offene Posten), sondern die Daten in der Struktur <RouteLink to="/referenz/refs/ft.refdata.einzbuch.html" title="Einzelbuchung">Einzelbuchungen</RouteLink>. Die Werte in den Einzelbuchungen werden pro Kombination Konto/Kostenstelle/Kostenträger zusammengefasst.</p>
<p>Pfad: <code v-pre>Buchhaltung → Verarbeitungsläufe → Aggregierte Buchungen generieren</code></p>
<h2 id="datenfelder" tabindex="-1"><a class="header-anchor" href="#datenfelder"><span>Datenfelder</span></a></h2>
<dl>
<dt>Journalnummer</dt>
<dd>Journalnummer der Aggregierung, vom System automatisch vergeben</dd>
<dt>Periode</dt>
<dd>Journal berücksichtigt Buchungen bis und mit diesem Datum.</dd>
</dl>
<div class="hint-container tip">
<p class="hint-container-title">Anmerkung</p>
<p>Die Aggregierung muss innerhalb des gleichen Geschäftsjahres erfolgen. Falls die Periode sich über zwei Geschäftsjahre erstreckt, muss zuerst bis zum Ende des ersten Geschäftsjahres aggregiert werden und eine neue Aggregierung vom Anfang des zweiten Geschäftsjahres bis zum Tagesdatum durchgeführt werden.</p>
</div>
<dl>
<dt>Laufnummer</dt>
<dd>
<p>Identifikator bei mehreren Läufen innerhalb eines Tages</p>
</dd>
<dt>Status</dt>
<dd>
<p>Statusinformationen im Sinne eines Protokolls (wie auch alle folgenden Informationen eine reine Anzeige darstellen)</p>
</dd>
<dd>
<p>1 = Job erfasst; noch nicht verarbeitet; löschbar</p>
</dd>
<dd>
<p>2 = Verarbeitung angefangen</p>
</dd>
<dd>
<p>4 = Verarbeitung angefangen und abgebrochen</p>
</dd>
<dd>
<p>5 = Job erledigt; Protokoll gedruckt</p>
</dd>
<dd>
<p>X = Job erfolgreich abgeschlossen</p>
</dd>
<dt>Einzelbuchungen</dt>
<dd>
<p>Anzahl Einzelbuchungen, die in dieser Aggregierung verarbeitet wurden</p>
</dd>
<dt>aggreg.Buchungen</dt>
<dd>
<p>Anzahl aggregierter Buchungen, die in dieser Aggregierung vom Programm generiert wurden</p>
</dd>
<dt>Fibu-Datensätze</dt>
<dd>
<p>Anzahl aggregierter Buchungen, die an die Finanzbuchhaltung übergeben werden (in die Datei geschrieben werden)</p>
</dd>
<dt>von</dt>
<dd>
<p>Identifikator der aggregierten Buchung (wird autom. vergeben)</p>
</dd>
<dt>bis</dt>
<dd>
<p>Identifikator der aggregierten Buchung (wird autom. vergeben)</p>
</dd>
<dt>Mandanten</dt>
<dd>
<p>Einschränkung auf einen oder mehrere Mandant(en)</p>
</dd>
<dt>Datei</dt>
<dd>
<p>Dateinamen, vom System automatisch vergeben.</p>
</dd>
<dt>Letzter Druck</dt>
<dd>
<p>Journal wurde letztmals ausgedruckt am [Datum]</p>
</dd>
<dt>Druckdatei</dt>
<dd>
<p>Dateiname der Druckdatei. Es wird der Name übernommen, der im Standarddialog &quot;Drucken&quot; angegeben wird. Kann auch manuell angegeben werden, wenn das Journal nicht direkt ausgedruckt, sondern elektronisch aufbewahrt werden soll</p>
</dd>
<dt>Drucker</dt>
<dd>
<p>Name des Druckers, falls das Journal direkt gedruckt wurde</p>
</dd>
<dt>Fibu-Übergabe:</dt>
<dd>
<p>Eintrag in der Protokolldatei. Der Benutzer kann unter verarbeiten die erfolgreiche Übergabe an die Fibu manuell bestätigen.</p>
</dd>
<dt>EB-Journal gesetzt</dt>
<dd>
<p>Statusmeldung: wenn Job/Protokoll Status 3 und höher hat</p>
</dd>
<dt>AB-Journal gesetzt</dt>
<dd>
<p>Statusmeldung: wenn Job/Protokoll Status 4 und höher hat</p>
</dd>
<dt>Fibu-Datei erstellt</dt>
<dd>
<p>Statusmeldung: wenn die Exportdatei erstellt worden. Das Programm prüft, ob der Dateiname vorhanden ist. Erhält Status, wenn Job/Protokoll Status &quot;5&quot; und höher hat</p>
</dd>
<dt>Fibu-Datenübergabe</dt>
<dd>
<p>Statusmeldung: wenn eine manuelle Bestätigung des Benutzerserfolgt ist, welche die Datenübergabe an die Finanzbuchhaltung bestätigt</p>
</dd>
<dt>Journale gedruckt</dt>
<dd>
<p>Statusmeldung: wenn Job/Protokoll Status 6 oder 9 hat</p>
</dd>
<dt>Journale bereinigt</dt>
<dd>
<p>Statusmeldung: siehe im Detail Kapitel AB-Bereinigen</p>
</dd>
</dl>
<h2 id="funktionen" tabindex="-1"><a class="header-anchor" href="#funktionen"><span>Funktionen</span></a></h2>
<h3 id="aggregieren" tabindex="-1"><a class="header-anchor" href="#aggregieren"><span>Aggregieren</span></a></h3>
<p>Diese Funktion führt die Aggregierung in funtrade aus. Sämtliche noch nicht aggregierten Buchungen werden zusammengefasst und die Übergabe an die Finanzbuchhaltung vorbereitet.</p>
<p>Die Voreinstellungen können übernommen werden. Allenfalls die Journalnummer wählen, falls eine andere als die letzte gewünscht ist. Das FIBU Codeset wird durch ARENAE parametrisiert.</p>
<h3 id="buchungsjournal-drucken" tabindex="-1"><a class="header-anchor" href="#buchungsjournal-drucken"><span>Buchungsjournal drucken</span></a></h3>
<p>Nach Wahl der gewünschten Optionen wird das Journal ausgedruckt. Es kann auch mehrmals gedruckt werden.</p>
<p>Outputbeispiel</p>
<div class="language-text line-numbers-mode" data-highlighter="prismjs" data-ext="text"><pre v-pre><code><span class="line"></span>
<span class="line">Stamm: Einzelbuchungen                      07.04.2000 17:06:00 leh  Seite     0</span>
<span class="line">\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_</span>
<span class="line"></span>
<span class="line">                              EB-Buchungsjournal</span>
<span class="line">                        von Journalnummer: 1134</span>
<span class="line">                        bis Journalnummer: 1134</span>
<span class="line"></span>
<span class="line">                        detailliert anzeigen:</span>
<span class="line"></span>
<span class="line">                        vereinbarte Buchungen..: ja</span>
<span class="line">                        vereinnahmte Buchungen.: ja</span>
<span class="line">                        Zahlungen..............: ja</span>
<span class="line">                        Gutschriftsbewegungen..: ja</span>
<span class="line"></span>
<span class="line">                        vollständige Totalwerte: ja</span>
<span class="line"></span>
<span class="line"></span>
<span class="line">FaktNum     vereinbart    vereinnahmt        bezahlt gutgeschrieben</span>
<span class="line">------- -------------- -------------- -------------- --------------</span>
<span class="line"> 990801                         -2.00           2.00</span>
<span class="line"> 990802                          4.00          -4.00</span>
<span class="line">2372094         -27.50                         27.50</span>
<span class="line">2430935          20.00                        -20.00</span>
<span class="line">2430936          58.00                        -58.00</span>
<span class="line">2430937          78.00                        -78.00</span>
<span class="line">------- -------------- -------------- -------------- --------------</span>
<span class="line">                128.50           2.00        -130.50           0.00 TOTAL</span>
<span class="line">      6                                                             COUNT</span>
<span class="line"></span>
<span class="line">                      vollständige Totalwerte</span>
<span class="line">            vereinbart    vereinnahmt        bezahlt gutgeschrieben</span>
<span class="line">        -------------- -------------- -------------- --------------</span>
<span class="line">                128.50           2.00        -130.50           0.00</span>
<span class="line"></span>
<span class="line">Totalanzahl der Buchungen           19.00</span>
<span class="line">Debitoren Totaländerung              0.00</span>
<span class="line"></span></code></pre>
<div class="line-numbers" aria-hidden="true" style="counter-reset:line-number 0"><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div></div></div><p>Lesebeispiel: Mit Fakturanummer 990802 wurden 4 Franken vereinnahmt gebucht aus Spende. Die Fakturanummer 2372094 betrifft einen eigentlichen Auftrag, der Saldo auf dem Debitor verändert sich um Fr. 27.50 (nicht aus oder in das Gutschriftskonto).:</p>
<h3 id="aggregierung-ruckgangig-machen" tabindex="-1"><a class="header-anchor" href="#aggregierung-ruckgangig-machen"><span>Aggregierung rückgängig machen</span></a></h3>
<p>Der Aggregierungsprozess wird zurückgesetzt. Die im zurückgesetzten Job enthaltenen Einzelbuchungen werden im nächsten Aggregierungslauf wieder berücksichtigt.</p>
<h4 id="parameter" tabindex="-1"><a class="header-anchor" href="#parameter"><span>Parameter</span></a></h4>
<dl>
<dt>Journalnummer</dt>
<dd>Journalnummer angeben, die rückgängig gemacht werden soll. Es kann nur die jeweils letzte Journalnummer rückgängig gemacht werden.</dd>
</dl>
<h3 id="protokoll-drucken" tabindex="-1"><a class="header-anchor" href="#protokoll-drucken"><span>Protokoll drucken</span></a></h3>
<p>Mit dieser Funktion wird das Job-Protokoll mit den Parametrisierungen gedruckt</p>
<h2 id="siehe-auch" tabindex="-1"><a class="header-anchor" href="#siehe-auch"><span>Siehe auch</span></a></h2>
<p><RouteLink to="/manual/buchhaltung/buchungsexport.html" title="4. Buchungsexport (in Finanzbuchhaltung)">Buchungsexport</RouteLink>, <RouteLink to="/manual/buchhaltung/debitorenverwaltung.html#Debitorenverwaltung" title="1. Debitorenverwaltung">Debitorenverwaltung</RouteLink></p>
</div></template>


